import * as React from 'react'
import reduce from 'lodash/reduce'
import every from 'lodash/every'
import map from 'lodash/map'
import {fromMaybe} from '@freckle/maybe'
import {isNullOrUndefinedOrEmptyString} from '@frontrow/console/src/entities/helpers/common-helper'
import {type StarStatus} from '@frontrow/console/src/entities/models/school'
import {type NonEmptyArray} from '@freckle/non-empty'

import {
  type DetailedSchoolT,
  type WeakRenaissanceSchoolClientT
} from '@frontrow/console/src/models/school'
import {
  validateRenaissanceSchoolClient,
  type ValidationResultT
} from '@frontrow/console/src/helpers/validations/school'
import {
  type BulkUpdateSchoolConfigurationDataT,
  type SchoolBatchUpdateResponseT
} from '@frontrow/console/src/models/school-batch-update'
import {BatchUpdateResponseNotification} from '@frontrow/console/src/components/pages/bulk-edit-school-configuration/batch-update-response-notification'
import {BulkEditSchoolConfiguration} from '@frontrow/console/src/components/pages/bulk-edit-school-configuration/bulk-edit-school-configuration'
import {detailedToBasicSchool} from '@frontrow/console/src/models/school'
import {canBulkEdit} from '@frontrow/console/src/components/pages/bulk-edit-school-configuration/logic'

type Props = {
  schoolData: NonEmptyArray<DetailedSchoolT>
  batchUpdateSchoolConfiguration: (
    updatedSchools: Array<BulkUpdateSchoolConfigurationDataT>
  ) => void
  batchUpdateInProgress: boolean
  batchUpdateResponse: SchoolBatchUpdateResponseT | undefined | null
}

type State = {
  clientId: string
  rpId: string
  baseUrl: string
  renaissancePlatform: string
  starStatus: StarStatus
  isSharedRosteringEnabled: boolean
  allSchoolsHaveRenaissanceAsIdp: boolean
  shouldSetRosteringSourceToRenaissance: boolean
  rpIdentifiersToUpdate: {
    [schoolId: string]: string
  }
}

/** @internal */
export function deriveState(props: Props): State {
  // Because we only allow bulk editing when renaissanceSchoolClient fields
  // are same for all schools, we can use the values from any school
  // as a starting point
  const firstSchoolData = props.schoolData[0]
  const {
    clientId,
    rpid: rpId,
    platform,
    baseUrl,
    starStatus,
    sharedRosteringEnabled: isSharedRosteringEnabled
  } = fromMaybe(
    () => ({
      platform: '',
      clientId: '',
      rpid: '',
      baseUrl: '',
      starStatus: 'star_unset' as const,
      sharedRosteringEnabled: false
    }),
    firstSchoolData.renaissanceSchoolClient
  )

  const allSchoolsHaveRenaissanceAsIdp = every(
    props.schoolData,
    school => school.activeIdp === 'renaissance'
  )

  return {
    clientId,
    rpId,
    baseUrl,
    renaissancePlatform: platform,
    starStatus,
    isSharedRosteringEnabled,
    allSchoolsHaveRenaissanceAsIdp,
    shouldSetRosteringSourceToRenaissance: false,
    rpIdentifiersToUpdate: reduce(
      props.schoolData,
      (acc, school) => {
        acc[school.id.toString()] = fromMaybe(() => '', school.renaissanceRpIdentifier) as string
        return acc
      },
      {} as Record<string, string>
    )
  }
}

export function BulkEditSchoolConfigurationContainer(props: Props): React.ReactElement {
  const {schoolData, batchUpdateSchoolConfiguration, batchUpdateInProgress, batchUpdateResponse} =
    props

  const initialState = React.useMemo(() => deriveState(props), [props])

  const [clientId, setClientId] = React.useState<string>(initialState.clientId)
  const [rpId, setRpId] = React.useState<string>(initialState.rpId)
  const [baseUrl, setBaseUrl] = React.useState<string>(initialState.baseUrl)
  const [renaissancePlatform, setRenaissancePlatform] = React.useState<string>(
    initialState.renaissancePlatform
  )
  const [starStatus, setStarStatus] = React.useState<StarStatus>(initialState.starStatus)
  const [isSharedRosteringEnabled, setIsSharedRosteringEnabled] = React.useState<boolean>(
    initialState.isSharedRosteringEnabled
  )
  const [allSchoolsHaveRenaissanceAsIdp, _setAllSchoolsHaveRenaissanceAsIdp] =
    React.useState<boolean>(initialState.allSchoolsHaveRenaissanceAsIdp)
  const [shouldSetRosteringSourceToRenaissance, setShouldSetRosteringSourceToRenaissance] =
    React.useState<boolean>(initialState.shouldSetRosteringSourceToRenaissance)
  const [rpIdentifiersToUpdate, setRpIdentifiersToUpdate] = React.useState<{
    [schoolId: string]: string
  }>(initialState.rpIdentifiersToUpdate)

  const onUpdateClientId = (clientId?: string | null) => {
    setClientId(fromMaybe(() => '', clientId))
  }

  const onUpdateRpId = (rpId?: string | null) => {
    setRpId(fromMaybe(() => '', rpId))
  }

  const onUpdateBaseUrl = (baseUrl?: string | null) => {
    setBaseUrl(fromMaybe(() => '', baseUrl))
  }

  const onUpdateRenaissancePlatform = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newRenaissancePlatform = e.currentTarget.value
    setRenaissancePlatform(newRenaissancePlatform)
    setIsSharedRosteringEnabled(
      shouldSetRosteringSourceToRenaissance &&
        newRenaissancePlatform === 'renaissance_growth_platform'
    )
  }

  const onToggleShouldSetRosteringSourceToRenaissance = () => {
    const newShouldSetRosteringSourceToRenaissance = !shouldSetRosteringSourceToRenaissance
    setShouldSetRosteringSourceToRenaissance(newShouldSetRosteringSourceToRenaissance)
    setIsSharedRosteringEnabled(
      newShouldSetRosteringSourceToRenaissance &&
        renaissancePlatform === 'renaissance_growth_platform'
    )
  }

  const onUpdateStarStatus = (starStatus: StarStatus) => {
    setStarStatus(starStatus)
  }

  const onUpdateRpIdentifier = (schoolId: number, rpIdentifier?: string | null) => {
    setRpIdentifiersToUpdate(prevState => ({
      ...prevState,
      [schoolId]: fromMaybe(() => '', rpIdentifier)
    }))
  }

  const onUpdateSchools = () => {
    const updatedSchools = map(schoolData, school => ({
      ...detailedToBasicSchool(school),
      id: school.id,
      cleverSchoolId: shouldSetRosteringSourceToRenaissance ? null : school.cleverSchoolId,
      renaissanceRpIdentifier: rpIdentifiersToUpdate[school.id.toString()],
      renaissanceSchoolClient: {
        baseUrl,
        clientId,
        platform: renaissancePlatform,
        rpid: rpId,
        sharedRosteringEnabled: isSharedRosteringEnabled,
        starStatus
      },
      activeIdp: shouldSetRosteringSourceToRenaissance ? 'renaissance' : school.activeIdp,
      shouldDeleteCleverAndClasslinkAssociations:
        shouldSetRosteringSourceToRenaissance &&
        school.activeIdp !== null &&
        school.activeIdp !== undefined &&
        // @ts-ignore Potential error with the code
        school.activeIdp !== 'none' &&
        school.activeIdp !== 'renaissance'
    }))

    batchUpdateSchoolConfiguration(updatedSchools)
  }

  const getWeakRenaissanceSchoolClientFromState = (): WeakRenaissanceSchoolClientT => {
    return {
      platform: renaissancePlatform,
      clientId,
      rpid: rpId,
      baseUrl,
      starStatus,
      sharedRosteringEnabled: isSharedRosteringEnabled
    }
  }

  const canEditForm = canBulkEdit(schoolData)

  if (batchUpdateResponse !== null && batchUpdateResponse !== undefined) {
    return <BatchUpdateResponseNotification batchUpdateResponse={batchUpdateResponse} />
  }

  const weakRenaissanceSchoolClient = getWeakRenaissanceSchoolClientFromState()
  const renaissanceSchoolClientValidationResult: ValidationResultT =
    validateRenaissanceSchoolClient(weakRenaissanceSchoolClient)

  const missingRenaissanceClientConfig =
    isNullOrUndefinedOrEmptyString(renaissancePlatform) ||
    isNullOrUndefinedOrEmptyString(baseUrl) ||
    isNullOrUndefinedOrEmptyString(rpId)

  const needsStarIntegrationFields = starStatus === 'star_enabled' && missingRenaissanceClientConfig

  const needsRenaissanceIdPFields =
    shouldSetRosteringSourceToRenaissance &&
    (missingRenaissanceClientConfig || renaissancePlatform !== 'renaissance_growth_platform')

  const submitDisabled =
    needsStarIntegrationFields ||
    needsRenaissanceIdPFields ||
    renaissanceSchoolClientValidationResult.tag !== 'valid' ||
    batchUpdateInProgress

  const showRenaissanceIDPError =
    shouldSetRosteringSourceToRenaissance &&
    (missingRenaissanceClientConfig || renaissancePlatform !== 'renaissance_growth_platform')

  const {districtIdsSet, schoolsWithRpIdentifier, schoolsWithoutRpIdentifier} = reduce(
    schoolData,
    (acc, school) => {
      acc.districtIdsSet.add(school.districtId)
      if (school.renaissanceRpIdentifier !== null && school.renaissanceRpIdentifier !== undefined) {
        acc.schoolsWithRpIdentifier.push(school as never)
      } else {
        acc.schoolsWithoutRpIdentifier.push(school as never)
      }
      return acc
    },
    {
      districtIdsSet: new Set(),
      schoolsWithRpIdentifier: [],
      schoolsWithoutRpIdentifier: []
    }
  )

  return (
    <BulkEditSchoolConfiguration
      canEditForm={canEditForm}
      districtIdsSet={districtIdsSet as Set<number>}
      schoolData={schoolData}
      shouldSetRosteringSourceToRenaissance={shouldSetRosteringSourceToRenaissance}
      allSchoolsHaveRenaissanceAsIdp={allSchoolsHaveRenaissanceAsIdp}
      weakRenaissanceSchoolClient={weakRenaissanceSchoolClient}
      starStatus={starStatus}
      renaissancePlatform={renaissancePlatform}
      needsStarIntegrationFields={needsStarIntegrationFields}
      showRenaissanceIDPError={showRenaissanceIDPError}
      clientId={clientId}
      rpId={rpId}
      baseUrl={baseUrl}
      schoolsWithoutRpIdentifier={schoolsWithoutRpIdentifier}
      schoolsWithRpIdentifier={schoolsWithRpIdentifier}
      batchUpdateInProgress={batchUpdateInProgress}
      submitDisabled={submitDisabled}
      rpIdentifiersToUpdate={rpIdentifiersToUpdate}
      onUpdateClientId={onUpdateClientId}
      onUpdateBaseUrl={onUpdateBaseUrl}
      onUpdateRpId={onUpdateRpId}
      onUpdateRenaissancePlatform={onUpdateRenaissancePlatform}
      onUpdateStarStatus={onUpdateStarStatus}
      onUpdateSchools={onUpdateSchools}
      onUpdateRpIdentifier={onUpdateRpIdentifier}
      onToggleShouldSetRosteringSourceToRenaissance={onToggleShouldSetRosteringSourceToRenaissance}
    />
  )
}
